import React, { useState, useEffect } from 'react';
import { Package, CheckCircle, Calendar, User as UserIcon, Truck, AlertCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const MyAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [expandedIssues, setExpandedIssues] = useState(new Set());

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/warehouse/warehouse-issues/my-assignments/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          withCredentials: true,
        }
      );
      setAssignments(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      setError('Failed to load assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const toggleIssue = (issueId) => {
    const newExpanded = new Set(expandedIssues);
    if (newExpanded.has(issueId)) {
      newExpanded.delete(issueId);
    } else {
      newExpanded.add(issueId);
    }
    setExpandedIssues(newExpanded);
  };

  const handleConfirmIndividualItem = async (issueId, itemId, individualItemId) => {
    if (!window.confirm('Are you sure you want to confirm receipt of this item?')) {
      return;
    }

    try {
      setConfirmingId(individualItemId);
      await axios.post(
        `${API_BASE_URL}/warehouse/warehouse-issues/${issueId}/confirm-individual-item/`,
        {
          item_id: itemId,
          individual_item_id: individualItemId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          withCredentials: true,
        }
      );

      await fetchAssignments();
      alert('Receipt confirmed successfully!');
    } catch (err) {
      console.error('Error confirming receipt:', err);
      alert(err.response?.data?.error || 'Failed to confirm receipt. Please try again.');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleConfirmWholeItem = async (issueId, itemId) => {
    if (!window.confirm('Are you sure you want to confirm receipt of this entire item?')) {
      return;
    }

    try {
      setConfirmingId(itemId);
      await axios.post(
        `${API_BASE_URL}/warehouse/warehouse-issues/${issueId}/confirm-item/`,
        {
          item_id: itemId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          withCredentials: true,
        }
      );

      await fetchAssignments();
      alert('Receipt confirmed successfully!');
    } catch (err) {
      console.error('Error confirming receipt:', err);
      alert(err.response?.data?.error || 'Failed to confirm receipt. Please try again.');
    } finally {
      setConfirmingId(null);
    }
  };

  const checkAllItemsReceived = (issue) => {
    if (!issue.items || issue.items.length === 0) return false;

    for (const item of issue.items) {
      if (item.individual_items && item.individual_items.length > 0) {
        const hasUnconfirmed = item.individual_items.some(indItem => indItem.status === 'issued');
        if (hasUnconfirmed) return false;
      } else {
        if (!item.notes || !item.notes.includes('[RECEIVED]')) {
          return false;
        }
      }
    }

    return true;
  };

  const getIssueStatus = (issue) => {
    if (checkAllItemsReceived(issue)) {
      return 'received';
    }
    return issue.status;
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'issued':
        return 'bg-sky-100 text-sky-800 border-sky-200';
      case 'received':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'partially_returned':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'fully_returned':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'issued': return 'Issued';
      case 'received': return 'Received';
      case 'partially_returned': return 'Partially Returned';
      case 'fully_returned': return 'Returned';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-3 border-slate-200 border-t-brand-500 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading assignments...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-red-800">Error</h3>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">My Assignments</h1>
        <p className="text-sm text-slate-500 mt-1">Items assigned to you from the warehouse</p>
      </div>

      {assignments.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-900 mb-2">No Pending Assignments</h3>
          <p className="text-sm text-slate-500">You don't have any items waiting for confirmation at the moment.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-steel-100 border-b-2 border-brand-500">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Assigned By</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Assigned Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Expected Return</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assignments.map((issue) => (
                  <React.Fragment key={issue.id}>
                    <tr
                      onClick={() => toggleIssue(issue.id)}
                      className="hover:bg-brand-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {expandedIssues.has(issue.id) ? (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )}
                          <Package className="h-5 w-5 text-slate-400" />
                          <div>
                            <div className="text-sm font-medium text-slate-900">{issue.issue_number}</div>
                            <div className="text-xs text-slate-400">{issue.items?.length || 0} items</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-slate-900">
                          {issue.items?.reduce((sum, item) => sum + (item.quantity_issued || 0), 0) || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-700">{issue.issued_by_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-600">{formatDate(issue.issued_at)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-600">{formatDate(issue.expected_return_date)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {issue.project_name ? (
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-slate-400" />
                            <span className="text-sm text-slate-700">{issue.project_name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getStatusStyles(getIssueStatus(issue))}`}>
                          {getStatusLabel(getIssueStatus(issue))}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-brand-600 font-medium">
                          {expandedIssues.has(issue.id) ? 'Hide Details' : 'View Details'}
                        </span>
                      </td>
                    </tr>
                    {expandedIssues.has(issue.id) && issue.items && issue.items.length > 0 && (
                      <tr className="bg-slate-50">
                        <td colSpan="8">
                          <div className="p-4">
                            <h4 className="text-sm font-bold text-slate-900 mb-3">Items in this assignment:</h4>
                            <div className="space-y-3">
                              {issue.items.map((item, itemIdx) => (
                                <div key={itemIdx} className="bg-white border border-slate-200 rounded-xl p-4">
                                  <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-start gap-3 flex-1">
                                      {item.item_picture && (
                                        <img
                                          src={
                                            item.item_picture.startsWith('http')
                                              ? item.item_picture
                                              : `${(process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '')}${item.item_picture.startsWith('/') ? item.item_picture : `/${item.item_picture}`}`
                                          }
                                          alt={item.item_name}
                                          className="w-16 h-16 object-cover rounded-lg border border-slate-200 flex-shrink-0"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      )}
                                      <div className="flex-1">
                                        <h5 className="text-sm font-bold text-slate-900">{item.item_name || 'N/A'}</h5>
                                        {item.item_sku && (
                                          <p className="text-xs text-slate-500">SKU: {item.item_sku}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                      <div className="text-slate-600">
                                        Total Qty: <span className="font-bold text-slate-900">{item.quantity_issued}</span>
                                      </div>
                                      {item.quantity_outstanding > 0 && (
                                        <div className="text-orange-600">
                                          Outstanding: <span className="font-bold">{item.quantity_outstanding}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Confirm button for items WITHOUT individual tracking */}
                                  {(!item.individual_items || item.individual_items.length === 0) && (
                                    <div className="mt-3 pt-3 border-t border-slate-100">
                                      {item.notes && item.notes.includes('[RECEIVED]') ? (
                                        <div className="flex items-center gap-2 text-emerald-600">
                                          <CheckCircle className="h-4 w-4" />
                                          <span className="text-sm font-medium">Receipt confirmed</span>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleConfirmWholeItem(issue.id, item.id);
                                          }}
                                          disabled={confirmingId === item.id}
                                          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-sm font-bold rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {confirmingId === item.id ? (
                                            <>
                                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                              Confirming...
                                            </>
                                          ) : (
                                            <>
                                              <CheckCircle className="h-4 w-4" />
                                              Confirm Receipt of All Items
                                            </>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  )}

                                  {/* Individual items with custom fields */}
                                  {item.individual_items && item.individual_items.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                                      {item.individual_items.map((indItem, indIdx) => (
                                        <div key={indIdx} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
                                          <div className="flex items-center flex-wrap gap-3">
                                            <span className="text-xs font-mono font-medium text-slate-700">#{indItem.id}</span>
                                            {indItem.custom_fields && Object.entries(indItem.custom_fields).map(([fieldName, fieldValue]) => (
                                              <div key={fieldName} className="text-sm">
                                                <span className="text-slate-500">{fieldName.split('_')[0]}:</span>{' '}
                                                <span className="font-semibold text-slate-900">{fieldValue}</span>
                                              </div>
                                            ))}
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                              indItem.status === 'issued' ? 'bg-sky-100 text-sky-700' :
                                              indItem.status === 'received' ? 'bg-emerald-100 text-emerald-700' :
                                              'bg-slate-100 text-slate-700'
                                            }`}>
                                              {indItem.status}
                                            </span>
                                          </div>
                                          {indItem.status === 'issued' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleConfirmIndividualItem(issue.id, item.id, indItem.id);
                                              }}
                                              disabled={confirmingId === indItem.id}
                                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs font-bold rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {confirmingId === indItem.id ? (
                                                <>
                                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                  Confirming...
                                                </>
                                              ) : (
                                                <>
                                                  <CheckCircle className="h-3 w-3" />
                                                  Confirm Receipt
                                                </>
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {assignments.length > 0 && (
        <div className="mt-6 bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-sky-900">Important Information</h4>
            <p className="text-sm text-sky-700 mt-1">Please confirm receipt of items once you physically receive them:</p>
            <ul className="mt-2 ml-4 list-disc text-sm text-sky-700 space-y-1">
              <li>For items with serial numbers - confirm each individual item</li>
              <li>For bulk items - confirm the entire shipment</li>
              <li>After confirmation, status will change to "Received"</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAssignments;
